import { CODE_SETTINGS } from '#app/utils/code-settings'
import { siteContent } from './content'
/**
 * Pure derivations behind the auth screens (app/components/site/auth). The
 * components are thin rendering over these, so the behaviour that matters
 * (the resend countdown, the OAuth redirect, the consent sentence, the
 * cross-links that keep `redirectTo` and `invitation`) is unit tested here.
 */

import type { Audience } from './site-links'

/** The resend countdown (CODE_SETTINGS.resendSeconds). */
export const RESEND_SECONDS = CODE_SETTINGS.resendSeconds

/**
 * Whole seconds left in a countdown that started at `startedAt` (ms) and
 * runs for `seconds`, as of `now` (ms). Clock skew (a `now` before
 * `startedAt`) counts as "just started"; it never goes below zero.
 */
export function remainingSeconds(
	startedAt: number,
	now: number,
	seconds: number = RESEND_SECONDS,
): number {
	const elapsedMs = Math.max(0, now - startedAt)
	return Math.max(0, seconds - Math.floor(elapsedMs / 1000))
}

/**
 * Where the client started counting one resend countdown. `startedAt` is the
 * route's server timestamp (it only identifies which countdown this is);
 * `clientStart` is the client's own `Date.now()` when that countdown first
 * mounted on the client.
 */
export type ResendAnchor = { startedAt: number; clientStart: number }

/**
 * Whole seconds left on the resend countdown identified by `startedAt`,
 * measured entirely on the client's clock: elapsed time is
 * `clientNow - anchor.clientStart`, so a device clock that disagrees with
 * the server's never freezes or skips the countdown. Without an anchor for
 * this `startedAt` (the server render, the hydrating render, the first
 * render after a resend) it is the full length, which keeps the server and
 * the first client render identical.
 */
export function resendSecondsLeft(
	startedAt: number,
	anchor: ResendAnchor | null,
	clientNow: number,
	seconds: number = RESEND_SECONDS,
): number {
	if (anchor === null || anchor.startedAt !== startedAt) return seconds
	return remainingSeconds(anchor.clientStart, clientNow, seconds)
}

/**
 * Screen reader text for the resend line. It is the only content of the
 * line's live region and changes once, when the countdown runs out (the
 * ticking text itself is not announced).
 */
export const RESEND_READY_ANNOUNCEMENT = siteContent.auth.verify.resendReady

/** `mm:ss`, zero padded, for the resend line ("Resend Code in: 00:30"). */
export function formatCountdown(totalSeconds: number): string {
	const whole = Math.max(0, Math.floor(totalSeconds))
	const m = Math.floor(whole / 60)
	const s = whole % 60
	return `${m < 10 ? '0' : ''}${m}:${s < 10 ? '0' : ''}${s}`
}

/**
 * The `redirectTo` a provider button (Google, Apple, Facebook) posts to
 * `/auth/:provider`. The provider's callback cannot carry our own params, so
 * the audience (and, for an invited landlord team member, the invitation
 * token) ride along on the redirect that is stored in a cookie until the
 * callback reads it back (`parseProviderRedirect`). Without a destination
 * it falls back to `/` so the signals still travel.
 */
export function providerRedirectTo(
	redirectTo: string | null | undefined,
	userType: Audience,
	teamInvite?: string | null,
): string {
	const base = redirectTo && redirectTo.trim() !== '' ? redirectTo : '/'
	const params = new URLSearchParams({ userType })
	if (teamInvite) params.set('teamInvite', teamInvite)
	return `${base}${base.includes('?') ? '&' : '?'}${params}`
}

/**
 * Where a social sign-in from a login or signup page should land. A
 * co-applicant `invitation` always wins, exactly as it does for the
 * password login and sign-up (both go to the screening invitation).
 */
export function socialRedirectTarget(
	redirectTo: string | null | undefined,
	invitation: string | null | undefined,
): string | null | undefined {
	return invitation
		? `/tenant/screening-invitation/${encodeURIComponent(invitation)}`
		: redirectTo
}

export type ConsentSegment =
	| { kind: 'text'; text: string }
	| { kind: 'link'; to: 'terms' | 'privacy'; text: string }

/**
 * Splits the consent sentence from content.ts around the two phrases that
 * become links. Phrases that are missing from the sentence simply do not
 * become links, so a copy edit can never break the markup.
 */
export function consentSegments(
	sentence: string,
	labels: { terms: string; privacy: string },
): ConsentSegment[] {
	const targets = [
		{ to: 'terms' as const, text: labels.terms },
		{ to: 'privacy' as const, text: labels.privacy },
	]
	const segments: ConsentSegment[] = []
	let rest = sentence
	while (rest.length > 0) {
		let best: { index: number; to: 'terms' | 'privacy'; text: string } | null =
			null
		for (const target of targets) {
			if (!target.text) continue
			const index = rest.indexOf(target.text)
			if (index !== -1 && (best === null || index < best.index)) {
				best = { index, ...target }
			}
		}
		if (best === null) {
			segments.push({ kind: 'text', text: rest })
			break
		}
		if (best.index > 0) {
			segments.push({ kind: 'text', text: rest.slice(0, best.index) })
		}
		segments.push({ kind: 'link', to: best.to, text: best.text })
		rest = rest.slice(best.index + best.text.length)
	}
	return segments
}

/**
 * A path with the given search params appended, skipping empty ones. The
 * login and signup screens link to each other with this so `redirectTo` and
 * `invitation` survive the hop (the old routes built these by hand).
 */
export function withSearchParams(
	path: string,
	params: Record<string, string | null | undefined>,
): string {
	const search = new URLSearchParams()
	for (const [key, value] of Object.entries(params)) {
		if (value != null && value !== '') search.set(key, value)
	}
	const query = search.toString()
	if (!query) return path
	return `${path}${path.includes('?') ? '&' : '?'}${query}`
}

/** Number of boxes the OTP field draws: `/verify` issues six digits. */
export const OTP_LENGTH = 6

export type VerifySublineCopy = {
	sub: string
	subGeneric: string
	subTwoFactor: string
}

export type VerifySubline =
	| { kind: 'target'; lead: string; target: string }
	| { kind: 'text'; text: string }

/**
 * The line under the `/verify` heading. It names the target only when this
 * type really emailed a code to it: onboarding, change-email and
 * social-email always do,
 * reset-password only when the user typed an email (it also accepts a
 * username). 2FA codes come from the user's authenticator app (its target
 * is the internal user id), so it gets its own sentence. Anything else
 * gets the generic email sentence.
 */
export function verifySubline(
	type: string | null | undefined,
	target: string | null | undefined,
	copy: VerifySublineCopy,
): VerifySubline {
	if (type === '2fa') return { kind: 'text', text: copy.subTwoFactor }
	const value = target?.trim() ?? ''
	const emailsTarget =
		value !== '' &&
		(type === 'onboarding' ||
			type === 'change-email' ||
			type === 'social-email' ||
			(type === 'reset-password' && value.includes('@')))
	if (emailsTarget) return { kind: 'target', lead: copy.sub, target: value }
	return { kind: 'text', text: copy.subGeneric }
}

/**
 * Whether `/verify` offers "Resend Code": only for a password-reset code,
 * the one code that page still sends (the server refuses any other resend).
 * Not for 2FA: nothing is sent, the code comes from the authenticator app.
 * Sign-up codes resend on /verify-account.
 */
export function verifyAllowsResend(type: string | null | undefined): boolean {
	return type === 'reset-password'
}
